import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit, Self, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIcon } from '@angular/material/icon';
import { Router } from '@angular/router';
import { AuthenticationService } from '@flexyoo/auth';
import { EmployeePlanInvitationDataAccessService } from '@flexyoo/employee/data-access';
import { UserAgreementType } from '@flexyoo/api/models';
import { EmployeeApiService } from '@flexyoo/api/services';
import { IEmployeeSettings } from '@flexyoo/employee/models';
import {
  EmployeeSettingsActions,
  employeeSettingsSelectors,
  quillUserAgreementSignNotificationsActions,
} from '@flexyoo/employee/store';
import {
  getEmployeeDefaultRoute,
  isCafeteriaPlanEnrolled,
  isMobilityBudgetEnrolled,
} from '@flexyoo/employee/util';
import { FlexyooLetDirective } from '@flexyoo/shared/directives';
import { UserNotificationType } from '@flexyoo/shared/enums';
import { UserNotificationsService } from '@flexyoo/shared/services';
import { PopUpService } from '@flexyoo/shared/ui';
import { tenantSettingsSelectors } from '@flexyoo/tenant/store';
import { userSettingsSelectors } from '@flexyoo/user/store';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  merge,
  of,
  pairwise,
  shareReplay,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs';
import { UserNotificationsDataAccessService } from 'src/app/libs/shared/services/user-notifications.data-access.service';
import { NgOnDestroy } from 'src/app/shared/ng-on-destroy.service';
import {
  MobilityBudgetPhoneStepComponent,
  UserAgreementService,
  UserAgreementSignComponent,
  UserAgreementSignUploadBlockComponent,
  UserAgreementTypeSelectionComponent,
} from '../../user-agreement';

@Component({
  selector: 'app-agreement-sign-page',
  templateUrl: './agreement-sign-page.component.html',
  styleUrl: './agreement-sign-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatCardModule,
    TranslateModule,
    FlexyooLetDirective,
    AsyncPipe,
    UserAgreementTypeSelectionComponent,
    MobilityBudgetPhoneStepComponent,
    UserAgreementSignUploadBlockComponent,
    UserAgreementSignComponent,
    MatButtonModule,
    MatIcon,
  ],
  providers: [NgOnDestroy],
})
export class AgreementSignPageComponent implements OnInit {
  private readonly popUpService = inject(PopUpService);
  private readonly userNotificationsService = inject(UserNotificationsService);
  private readonly userNotificationsDataAccessService = inject(UserNotificationsDataAccessService);
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly tenantSettings$ = this.store.select(
    tenantSettingsSelectors.selectTenantSettings
  );
  protected readonly employeeSettings$ = this.store.select(
    employeeSettingsSelectors.selectEmployeeSettings
  );
  private readonly authService = inject(AuthenticationService);
  private readonly employeeApi = inject(EmployeeApiService);
  private readonly employeePlanInvitationService = inject(EmployeePlanInvitationDataAccessService);
  private readonly userAgreementService = inject(UserAgreementService);
  private readonly actions$ = inject(Actions);

  protected readonly areBothPlansEnabled$ = this.employeeSettings$.pipe(
    map(
      (settings) =>
        settings?.isPrimesAndBonusesFinancingPlan && settings?.isSalarySacrificeFinancingPlan
    ),
    distinctUntilChanged()
  );

  protected readonly isCafeteriaPlanEnrolled$ = this.employeeSettings$.pipe(
    map((settings) => isCafeteriaPlanEnrolled(settings)),
    distinctUntilChanged()
  );

  private readonly employeeRefresh$ = merge(
    of(undefined),
    inject(Actions).pipe(ofType(quillUserAgreementSignNotificationsActions.received))
  );

  private readonly employeeAfterQuillNotification$ = this.employeeRefresh$.pipe(
    switchMap(() => this.employeeApi.getById(this.authService.getCurrentUser().EmployeeId)),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  private readonly isCafeteriaInvitationSent$ = this.employeeAfterQuillNotification$.pipe(
    map((employee) => !!employee?.CafeteriaInvitationDate),
    distinctUntilChanged()
  );

  private readonly isMobilityBudgetInvitationSent$ = this.employeeAfterQuillNotification$.pipe(
    map((employee) => !!employee?.MobilityBudgetInvitationDate),
    distinctUntilChanged()
  );

  protected readonly shouldSignCafeteriaAgreement$ = combineLatest([
    this.isCafeteriaPlanEnrolled$,
    this.isCafeteriaInvitationSent$,
    this.tenantSettings$,
  ]).pipe(
    map(
      ([isEnrolled, isInvited, tenantSettings]) =>
        isEnrolled && isInvited && tenantSettings?.useCafeteriaAgreement
    ),
    distinctUntilChanged()
  );

  protected readonly shouldSignMobilityBudgetAgreement$ = combineLatest([
    this.employeeSettings$,
    this.tenantSettings$,
    this.isMobilityBudgetInvitationSent$,
  ]).pipe(
    map(
      ([employeeSettings, tenantSettings, isInvited]) =>
        !!employeeSettings?.isMobilityBudgetFinancingPlan &&
        !!tenantSettings?.useMobilityBudgetAgreement &&
        isInvited
    ),
    distinctUntilChanged()
  );

  private readonly isCafeteriaQuillSignatureInProgress$ = combineLatest([
    this.employeeAfterQuillNotification$,
    this.employeeSettings$,
    this.tenantSettings$,
  ]).pipe(
    map(
      ([employee, employeeSettings, tenantSetting]) =>
        (tenantSetting?.useQuillItsMeSignature || tenantSetting?.useQuillBelgianEIDSignature) &&
        !!employee?.QuillDocumentId &&
        employeeSettings?.currentUserAgreementTypeInSignProcess !== null &&
        employeeSettings?.currentUserAgreementTypeInSignProcess !== undefined &&
        employeeSettings.currentUserAgreementTypeInSignProcess !== UserAgreementType.MobilityBudget
    ),
    distinctUntilChanged()
  );

  private readonly isMobilityQuillSignatureInProgress$ = combineLatest([
    this.employeeAfterQuillNotification$,
    this.employeeSettings$,
    this.tenantSettings$,
  ]).pipe(
    map(
      ([employee, employeeSettings, tenantSetting]) =>
        (tenantSetting?.useQuillItsMeSignature || tenantSetting?.useQuillBelgianEIDSignature) &&
        !!employee?.QuillDocumentId &&
        employeeSettings?.currentUserAgreementTypeInSignProcess === UserAgreementType.MobilityBudget
    ),
    distinctUntilChanged()
  );

  protected readonly isFirstLogin$ = this.employeeSettings$.pipe(
    filter((user) => !!user),
    map((employeeSettings) => !employeeSettings?.cafeteriaUserAgreement?.ApprovedDate),
    distinctUntilChanged()
  );

  protected readonly isMobilityFirstLogin$ = this.employeeSettings$.pipe(
    filter((user) => !!user),
    map((employeeSettings) => !employeeSettings?.mobilityBudgetUserAgreement?.ApprovedDate),
    distinctUntilChanged()
  );

  protected readonly showQuillSignatureType$ = this.tenantSettings$.pipe(
    map((settings) => settings?.useQuillBelgianEIDSignature || settings?.useQuillItsMeSignature),
    distinctUntilChanged()
  );
  protected readonly showManualSignatureType$ = this.tenantSettings$.pipe(
    map((settings) => settings?.useManualSignature),
    distinctUntilChanged()
  );

  protected readonly form = new FormGroup<{
    userAgreementType: FormControl<UserAgreementType | null>;
  }>({
    userAgreementType: new FormControl<UserAgreementType | null>(null, [Validators.required]),
  });

  protected readonly selectedCafeteriaUserAgreementType = signal<UserAgreementType | null>(null);

  protected readonly isSelectedTypeSigned$ = combineLatest([
    this.employeeSettings$.pipe(map((employeeSettings) => employeeSettings)),
    this.form.controls.userAgreementType.valueChanges.pipe(
      startWith(null),
      map(() => this.form.controls.userAgreementType.getRawValue())
    ),
  ]).pipe(
    map(
      ([employeeSettings, selectedUserAgreementType]) =>
        employeeSettings?.cafeteriaUserAgreement?.Type === selectedUserAgreementType &&
        employeeSettings?.cafeteriaUserAgreement?.ApprovedDate
    ),
    distinctUntilChanged()
  );

  protected readonly userRequestedCafeteriaSign$ = new BehaviorSubject<boolean>(false);
  protected readonly isSignBlockShown$ = combineLatest([
    this.userRequestedCafeteriaSign$,
    this.isCafeteriaQuillSignatureInProgress$,
    this.isSelectedTypeSigned$,
  ]).pipe(
    map(
      ([requested, inProgress, isSelectedTypeSigned]) =>
        (requested || inProgress) && !isSelectedTypeSigned
    ),
    distinctUntilChanged()
  );

  protected readonly isMobilitySelectedTypeSigned$ = this.employeeSettings$.pipe(
    map(
      (employeeSettings) =>
        employeeSettings?.mobilityBudgetUserAgreement?.Type === UserAgreementType.MobilityBudget &&
        !!employeeSettings?.mobilityBudgetUserAgreement?.ApprovedDate
    ),
    distinctUntilChanged()
  );

  protected readonly savedMobilePhoneNumber$ = this.store
    .select(userSettingsSelectors.selectUserSettings)
    .pipe(
      map((settings) => settings?.mobilePhoneNumber?.trim() || null),
      distinctUntilChanged()
    );

  protected readonly userRequestedMobilitySign$ = new BehaviorSubject<boolean>(false);
  protected readonly isMobilityPhoneConfirmed$ = new BehaviorSubject<boolean>(false);

  protected readonly isMobilityPhoneStepShown$ = combineLatest([
    this.userRequestedMobilitySign$,
    this.isMobilityQuillSignatureInProgress$,
    this.isMobilitySelectedTypeSigned$,
    this.savedMobilePhoneNumber$,
  ]).pipe(
    map(
      ([requested, inProgress, isSelectedTypeSigned, savedPhone]) =>
        !isSelectedTypeSigned && (requested || inProgress || !!savedPhone)
    ),
    distinctUntilChanged()
  );

  protected readonly isMobilityAgreementSignBlockShown$ = combineLatest([
    this.userRequestedMobilitySign$,
    this.isMobilityQuillSignatureInProgress$,
    this.isMobilitySelectedTypeSigned$,
    this.isMobilityPhoneConfirmed$,
    this.savedMobilePhoneNumber$,
  ]).pipe(
    map(
      ([requested, inProgress, isSelectedTypeSigned, isPhoneConfirmed, savedPhone]) =>
        (requested || inProgress) &&
        !isSelectedTypeSigned &&
        (inProgress || isPhoneConfirmed || !!savedPhone)
    ),
    distinctUntilChanged()
  );

  constructor(@Self() private readonly onDestroy$: NgOnDestroy) {
    this.employeeSettings$.pipe(takeUntil(this.onDestroy$)).subscribe((employeeSettings) => {
      this.seedCafeteriaUserAgreementType(employeeSettings);
    });

    this.form.controls.userAgreementType.valueChanges
      .pipe(distinctUntilChanged(), takeUntil(this.onDestroy$))
      .subscribe((value) => {
        this.selectedCafeteriaUserAgreementType.set(value);
        this.userRequestedCafeteriaSign$.next(false);
      });

    this.isCafeteriaQuillSignatureInProgress$
      .pipe(distinctUntilChanged(), takeUntil(this.onDestroy$))
      .subscribe((isInProgress) => {
        if (isInProgress) {
          this.userRequestedCafeteriaSign$.next(true);
        }
      });

    this.isCafeteriaQuillSignatureInProgress$
      .pipe(
        pairwise(),
        filter(([wasInProgress, isInProgress]) => wasInProgress && !isInProgress),
        takeUntil(this.onDestroy$)
      )
      .subscribe(() => {
        this.userRequestedCafeteriaSign$.next(false);
      });

    this.isMobilityQuillSignatureInProgress$
      .pipe(distinctUntilChanged(), takeUntil(this.onDestroy$))
      .subscribe((isInProgress) => {
        if (isInProgress) {
          this.userRequestedMobilitySign$.next(true);
          this.isMobilityPhoneConfirmed$.next(true);
        }
      });

    this.isMobilityQuillSignatureInProgress$
      .pipe(
        pairwise(),
        filter(([wasInProgress, isInProgress]) => wasInProgress && !isInProgress),
        takeUntil(this.onDestroy$)
      )
      .subscribe(() => {
        this.userRequestedMobilitySign$.next(false);
        this.isMobilityPhoneConfirmed$.next(false);
      });
  }

  public ngOnInit(): void {
    this.refreshSignProcessState();
    this.checkPlanChangedNotifications();
    this.showFirstLoginModals();
  }

  private showFirstLoginModals(): void {
    this.actions$
      .pipe(
        ofType(EmployeeSettingsActions.loadSuccess),
        take(1),
        switchMap((employeeSettings) =>
          combineLatest([
            of(employeeSettings),
            this.tenantSettings$.pipe(take(1)),
            this.areBothPlansEnabled$.pipe(take(1)),
            this.isCafeteriaPlanEnrolled$.pipe(take(1)),
            this.isCafeteriaInvitationSent$.pipe(take(1)),
            this.isMobilityBudgetInvitationSent$.pipe(take(1)),
            this.employeeAfterQuillNotification$.pipe(take(1)),
          ])
        ),
        takeUntil(this.onDestroy$)
      )
      .subscribe(
        ([
          employeeSettings,
          tenantSettings,
          areBothPlansEnabled,
          isCafeteriaEnrolled,
          isCafeteriaInvitationSent,
          isMobilityBudgetInvitationSent,
          employee,
        ]) => {
          if (!tenantSettings) {
            return;
          }

          const invitation =
            this.employeePlanInvitationService.mapEmployeeToPlanInvitation(employee);

          // First-login modals only when no plan is accessible yet. Dual-plan users with
          // one signed agreement land on that plan after login; the second plan is
          // announced via SignAdditionalUserAgreement, not these modals.
          const userAgreementState = this.userAgreementService.checkUserAgreementState(
            employeeSettings,
            tenantSettings,
            invitation
          );
          if (userAgreementState === 'ok') {
            return;
          }

          const cafeteriaAgreement = employeeSettings.cafeteriaUserAgreement;
          const mobilityAgreement = employeeSettings.mobilityBudgetUserAgreement;

          // Show only one modal: cafeteria (invited + unsigned) takes precedence over MB.
          if (
            isCafeteriaEnrolled &&
            isCafeteriaInvitationSent &&
            !cafeteriaAgreement?.ApprovedDate &&
            !cafeteriaAgreement?.AppFileId
          ) {
            this.showFirstLoginNotification(areBothPlansEnabled);
            return;
          }

          if (
            isMobilityBudgetEnrolled(employeeSettings) &&
            isMobilityBudgetInvitationSent &&
            !mobilityAgreement?.ApprovedDate &&
            !mobilityAgreement?.AppFileId
          ) {
            this.showMobilityFirstLoginNotification();
          }
        }
      );
  }

  private showFirstLoginNotification(areBothPlansEnabled: boolean): void {
    this.popUpService
      .openConfirmDialog({
        title: 'user-agreement-sign-page.first-login-modal-title',
        text: `user-agreement-sign-page.${
          areBothPlansEnabled ? 'multi-plans' : 'single-plan'
        }.first-login-modal-text`,
        textYes: 'base-layout.user-notifications.buttons.sign-now',
        skipNo: true,
      })
      .subscribe();
  }

  private showMobilityFirstLoginNotification(): void {
    this.popUpService
      .openConfirmDialog({
        title: 'user-agreement-sign-page.first-login-modal-title',
        text: 'user-agreement-sign-page.mobility-budget.first-login-modal-text',
        textYes: 'base-layout.user-notifications.buttons.sign-now',
        skipNo: true,
      })
      .subscribe();
  }

  private checkPlanChangedNotifications(): void {
    this.employeeSettings$
      .pipe(
        filter((employeeSettings) => !!employeeSettings),
        take(1),
        switchMap(() =>
          this.userNotificationsDataAccessService.getUserNotifications([
            UserNotificationType.NewFinancingPlanTypeAvailable,
          ])
        ),
        filter((notifs) => notifs.filter((n) => !n.closedDate).length > 0),
        switchMap((notifs) => {
          const notification = notifs[0];
          const { title, text } =
            this.userNotificationsService.getNotificationTitleAndText(notification);

          return this.popUpService
            .openConfirmDialog({
              title,
              text,
              textYes: 'base-layout.user-notifications.buttons.review-now',
              textNo: 'base-layout.user-notifications.buttons.remind-me-later',
            })
            .pipe(map((result) => ({ result, notification })));
        }),
        switchMap(({ result, notification }) =>
          combineLatest([
            this.employeeSettings$.pipe(take(1)),
            this.tenantSettings$.pipe(take(1)),
            this.employeeAfterQuillNotification$.pipe(take(1)),
          ]).pipe(
            switchMap(([employeeSettings, tenantSettings, employee]) =>
              this.userNotificationsService.closeNotificationAndReload([notification.id]).pipe(
                tap(() => {
                  if (!result) {
                    this.router.navigate([
                      getEmployeeDefaultRoute(
                        employeeSettings,
                        tenantSettings,
                        this.employeePlanInvitationService.mapEmployeeToPlanInvitation(employee)
                      ),
                    ]);
                  }
                })
              )
            )
          )
        ),
        takeUntil(this.onDestroy$)
      )
      .subscribe();
  }

  protected onMobilityPhoneConfirmed(): void {
    this.isMobilityPhoneConfirmed$.next(true);
  }

  private refreshSignProcessState(): void {
    this.store.dispatch(EmployeeSettingsActions.load());
  }

  private seedCafeteriaUserAgreementType(
    employeeSettings: IEmployeeSettings | null | undefined
  ): void {
    if (this.form.controls.userAgreementType.value !== null) {
      return;
    }

    const agreement = employeeSettings?.cafeteriaUserAgreement;
    const agreementType = agreement?.Type;
    const hasType = agreementType !== null && agreementType !== undefined;
    const hasRealAgreement = !!agreement?.AppFileId || !!agreement?.ApprovedDate;

    if (hasRealAgreement && hasType) {
      this.setCafeteriaUserAgreementType(agreementType);
      return;
    }

    if (
      !employeeSettings?.isPrimesAndBonusesFinancingPlan ||
      !employeeSettings?.isSalarySacrificeFinancingPlan
    ) {
      if (employeeSettings?.isPrimesAndBonusesFinancingPlan) {
        this.setCafeteriaUserAgreementType(UserAgreementType.Primes);
        return;
      }

      if (employeeSettings?.isSalarySacrificeFinancingPlan) {
        this.setCafeteriaUserAgreementType(UserAgreementType.SalarySacrifice);
      }
    }
  }

  private setCafeteriaUserAgreementType(value: UserAgreementType): void {
    this.form.controls.userAgreementType.setValue(value, { emitEvent: false });
    this.selectedCafeteriaUserAgreementType.set(value);
  }

  protected readonly UserAgreementType = UserAgreementType;
}
